/**
 * Stations runtime (M2 plan §2.7, §2.10, M2-14): the home system's stations at their `stations.json` poses, their
 * docking-bay frames from the `DOCK_BAY_0` socket and their static colliders.
 *
 * - **Pose.** Each station sits at its float64 data position, turned by `stationRotation` so the bay's out axis
 *   (socket −Z) points at `faceToward`, then rolled by `roll`. The rotation and the bounding radius come from the
 *   station's asset-manifest entry at creation (the GLB is deferred, the manifest is not), so poses, `nearest` and
 *   `pick` work before the models load.
 * - **attachModel** (the career feature calls it when a deferred station model arrives): rebuilds the pose from
 *   the loaded model's entry (authoritative: a swapped Meshy model brings its own socket and bounds), fills the bay
 *   frame and hangs the model's `COL_` collider off one fixed Rapier body at the station's float64 pose
 *   (`addStaticTrimesh(..., EID_STATION)`; convex hulls and balls are built the same way), then marks the station
 *   ready. Attaching again replaces the collider.
 * - **Queries** (`nearest`, `pick`) are allocation-free (D-014).
 * - **setStations** (M3 R22e, the Reach bridge): a system switch refills `list` in place with that system's stations,
 *   removes the old colliders, bumps `version` and re-attaches models already loaded; away from home the Reach streams
 *   the station colliders instead (`colliders: false`).
 *
 * Positions are float64 world metres; Rapier sees them only through the physics origin (D-011, D-013).
 */
import RAPIER from '@dimforge/rapier3d-compat'
import type { LoadedModel } from '../assets/loader'
import { logger } from '../core/logger'
import type { Quat, Vec3 } from '../core/math'
import type { ModelEntry } from '../data/assetSchema'
import type { StationDef } from '../data/m2Schemas'
import { bayFrameFromSocket, createBayFrame, socketOut, stationRotation, type BaySocket } from '../docking/bay'
import { EID_STATION, STATIC_COLLISION_GROUPS, type Physics } from '../physics/physics'
import { addStaticTrimesh, removeStaticColliders, type StaticColliders } from '../physics/statics'
import type { SpaceContext, StationRuntime, StationsRuntime } from './contracts'

/** The station's model entry in the asset manifest, or null. */
export function stationModelEntry(ctx: Pick<SpaceContext, 'game'>, def: Pick<StationDef, 'assetId'>): ModelEntry | null {
  const models = ctx.game.assets.models
  for (let i = 0; i < models.length; i++) if (models[i].id === def.assetId) return models[i]
  return null
}

/** The bay socket (`def.baySocket`, `DOCK_BAY_0`) of a model entry, or null. */
export function findBaySocket(entry: Pick<ModelEntry, 'sockets'>, name: string): BaySocket | null {
  const sockets = entry.sockets
  for (let i = 0; i < sockets.length; i++) if (sockets[i].name === name) return sockets[i]
  return null
}

/**
 * Bay-size side table (M10 P1e1): the `size` param of each station's bay socket (`"M"` on every station today),
 * set whenever the station is posed from a model entry. A side table, never a field on `BayFrame` or
 * `StationRuntime` (both live in `space/contracts.ts`). Docking reads it through `stationBaySize`.
 */
const baySizes = new WeakMap<StationRuntime, string>()

/** The `size` param of a station's bay socket, or undefined when the socket has none (or the station has no entry yet). */
export function stationBaySize(station: StationRuntime): string | undefined {
  return baySizes.get(station)
}

/** Radius round the station origin that bounds the whole model: |bounds centre| + bounds radius. */
export function stationRadius(entry: Pick<ModelEntry, 'bounds'>): number {
  const c = entry.bounds.center
  return Math.hypot(c[0], c[1], c[2]) + entry.bounds.radius
}

const outScratch: Vec3 = { x: 0, y: 0, z: 0 }
const faceScratch: Vec3 = { x: 0, y: 0, z: 0 }

/**
 * Sets `station.rot` (the bay's out axis toward `faceToward`, rolled by `roll`) and `station.radius` from a model
 * entry. Returns the bay socket, or null (logged) when the entry has none: the station then keeps its rotation.
 */
export function poseFromEntry(station: StationRuntime, entry: ModelEntry): BaySocket | null {
  const def = station.def
  station.radius = stationRadius(entry)
  const socket = findBaySocket(entry, def.baySocket)
  if (!socket) {
    logger.warn(`station ${def.id}: model ${entry.id} has no ${def.baySocket} socket; it cannot dock`)
    baySizes.delete(station)
    return null
  }
  const size = socket.params.size
  if (typeof size === 'string') baySizes.set(station, size)
  else baySizes.delete(station)
  faceScratch.x = def.faceToward[0]
  faceScratch.y = def.faceToward[1]
  faceScratch.z = def.faceToward[2]
  stationRotation(station.pos, faceScratch, def.roll, socketOut(socket, outScratch), station.rot)
  return socket
}

/** A convex-hull or ball station collider as one fixed body at the station's float64 pose (a trimesh uses statics.ts). */
function addStaticShapes(p: Physics, model: LoadedModel, pos: Readonly<Vec3>, rot: Readonly<Quat>): StaticColliders | null {
  const col = model.collider
  if (!col || col.kind === 'trimesh') return null
  const descs: RAPIER.ColliderDesc[] = []
  if (col.kind === 'ball') {
    descs.push(RAPIER.ColliderDesc.ball(col.radius))
  } else {
    for (const hull of col.hulls) {
      const desc = RAPIER.ColliderDesc.convexHull(hull)
      if (!desc) throw new Error(`station ${model.entry.id}: a COL_ convex hull is degenerate`)
      descs.push(desc)
    }
  }
  const body = p.world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed()
      .setTranslation(pos.x - p.origin.x, pos.y - p.origin.y, pos.z - p.origin.z)
      .setRotation({ x: rot.x, y: rot.y, z: rot.z, w: rot.w }),
  )
  const handles: number[] = []
  for (const desc of descs) {
    const handle = p.world.createCollider(desc.setCollisionGroups(STATIC_COLLISION_GROUPS), body).handle
    p.colliderEid.set(handle, EID_STATION)
    handles.push(handle)
  }
  return { body, handles }
}

/** A station's runtime record at its data position, posed from its manifest entry (unready until a model attaches). */
function stationFromDef(ctx: Pick<SpaceContext, 'game'>, def: StationDef): StationRuntime {
  const station: StationRuntime = {
    def,
    index: def.index,
    pos: { x: def.position[0], y: def.position[1], z: def.position[2] },
    rot: { x: 0, y: 0, z: 0, w: 1 },
    radius: 0,
    bay: null,
    ready: false,
  }
  const entry = stationModelEntry(ctx, def)
  if (entry) poseFromEntry(station, entry)
  else logger.warn(`station ${def.id}: asset ${def.assetId} is not in the manifest`)
  return station
}

export function createStationsRuntime(ctx: SpaceContext): StationsRuntime {
  const list: StationRuntime[] = ctx.data.stations.stations.map((def) => stationFromDef(ctx, def))
  const colliders: (StaticColliders | null)[] = list.map(() => null)
  /** False once the Reach streams the station colliders itself (`setStations(defs, { colliders: false })`, M3 R22e). */
  let buildColliders = true
  let version = 0

  const runtime: StationsRuntime = {
    list,
    get version(): number {
      return version
    },
    setStations(defs: readonly StationDef[], opts?: { readonly colliders?: boolean }): void {
      if (opts?.colliders !== undefined) buildColliders = opts.colliders
      const p = ctx.sim.physics
      for (let i = 0; i < colliders.length; i++) {
        const old = colliders[i]
        if (old) removeStaticColliders(p, old)
      }
      list.length = 0
      colliders.length = 0
      for (const def of defs) {
        list.push(stationFromDef(ctx, def))
        colliders.push(null)
      }
      version++
      for (let i = 0; i < list.length; i++) {
        const model = ctx.models.get(list[i].def.assetId)
        if (model) runtime.attachModel(i, model)
      }
    },
    attachModel(index: number, model: LoadedModel): void {
      const station = list[index] as StationRuntime | undefined
      if (!station) throw new Error(`attachModel: no station ${index}`)
      if (!(model as Partial<LoadedModel>).entry) {
        logger.warn(`station ${station.def.id}: attachModel got a model without an entry`)
        return
      }
      const socket = poseFromEntry(station, model.entry)
      if (!socket) return
      station.bay = bayFrameFromSocket(station.pos, station.rot, socket, station.bay ?? createBayFrame())
      const p = ctx.sim.physics
      const old = colliders[index]
      if (old) removeStaticColliders(p, old)
      colliders[index] = null
      const col = model.collider
      if (!buildColliders) {
        // The Reach streams this station's collider (EID_STATION) around the player (M3 R22e).
      } else if (col?.kind === 'trimesh') {
        colliders[index] = addStaticTrimesh(p, col, { position: station.pos, rotation: station.rot }, EID_STATION, model.entry.id)
      } else if (col) {
        colliders[index] = addStaticShapes(p, model, station.pos, station.rot)
      } else {
        logger.warn(`station ${station.def.id}: model ${model.entry.id} has no collider`)
      }
      station.ready = true
    },
    nearest(x: number, y: number, z: number): number {
      return nearestStation(list, x, y, z)
    },
    pick(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, coneRad: number, maxRange: number): number {
      return pickStation(list, ox, oy, oz, dx, dy, dz, coneRad, maxRange)
    },
  }
  return runtime
}

/**
 * Index of the station whose hull is nearest (x, y, z), or -1: the least `|p − centre| − radius` (scale build §3.3),
 * so a big station's near face beats a small station's nearer centre. Allocation-free.
 */
export function nearestStation(list: readonly StationRuntime[], x: number, y: number, z: number): number {
  let best = -1
  let bestD = Infinity
  for (let i = 0; i < list.length; i++) {
    const s = list[i]
    const p = s.pos
    const ddx = p.x - x
    const ddy = p.y - y
    const ddz = p.z - z
    const d = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz) - s.radius
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

/**
 * The station nearest the ray from (ox, oy, oz) along the unit (dx, dy, dz): its origin within `maxRange`, and
 * its angle off the ray within `coneRad` or within the station's own angular radius (a big station close by is
 * picked anywhere on its silhouette). The smallest angle wins; -1 when none qualifies. Allocation-free.
 */
export function pickStation(
  list: readonly StationRuntime[],
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dy: number,
  dz: number,
  coneRad: number,
  maxRange: number,
): number {
  let best = -1
  let bestAngle = Infinity
  for (let i = 0; i < list.length; i++) {
    const s = list[i]
    const tx = s.pos.x - ox
    const ty = s.pos.y - oy
    const tz = s.pos.z - oz
    const d = Math.hypot(tx, ty, tz)
    if (d > maxRange) continue
    let angle = 0
    let limit = coneRad
    if (d > 1e-9) {
      const cx = dy * tz - dz * ty
      const cy = dz * tx - dx * tz
      const cz = dx * ty - dy * tx
      angle = Math.atan2(Math.hypot(cx, cy, cz), dx * tx + dy * ty + dz * tz)
      const size = s.radius >= d ? Math.PI * 0.5 : Math.asin(s.radius / d)
      if (size > limit) limit = size
    }
    if (angle <= limit && angle < bestAngle) {
      bestAngle = angle
      best = i
    }
  }
  return best
}
